/**
 * StorageTab — storage / partitioning / engine-options surface for
 * `TableEditor`. Slice E1 lights up the partitioning trio (set partition
 * scheme + add partition + drop/detach partition); Slice E2 lights up the
 * tablespace / filegroup creation popover; Slice E3 lights up the engine-
 * specific table-options popover (DATA_COMPRESSION / DURABILITY / SET / RESET
 * / ENGINE / ROW_FORMAT / PCTFREE / STORAGE / etc.).
 *
 * Each row reads `<label> <current value> … <actions>`:
 *  - the label uses the BOUND ENGINE's vocabulary only (SQL Server says
 *    “Filegroup”, everyone else says “Tablespace”); the mixed
 *    “Tablespace / Filegroup” wording survives only in the engine-less
 *    read-only scaffolding, where there is no engine to pick a term from.
 *  - the value is the storage placement declared by the parsed statement.
 *    `DdlStructure` carries none of these (partitioning / tablespace / engine
 *    options all land inside the opaque ranges), so it currently always renders
 *    an em dash; wire it up here the day the extractor surfaces them.
 *  - actions are ordered enabled-first, so a disabled trigger never leads the
 *    row, and read-only labels are bare verbs (“Set…”, “Edit…”) because the
 *    row label already names the subject.
 *
 * The Slice 0.4 read-only path remains: when `editContext` is null the
 * affordances render disabled with read-only-reason tooltips, so existing
 * read-only tests keep passing.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.4 + § Slice E1 + § Slice E2 + § Slice E3.
 */
import { useMemo, type ReactNode } from 'react';
import { Database, Plus, Settings2 } from 'lucide-react';

import { cn } from '../../../../../lib/utils';
import type { QualifiedName, SqlEngine } from '../../../../sql-grammar/model';
import type { DdlStructure } from '../../../../sql-resolver/types/ddlEdit';
import { isAddPartitionSupported } from '../../edit/edits/addPartition';
import { isDropPartitionSupported } from '../../edit/edits/dropPartition';
import { isSetPartitionSchemeSupported } from '../../edit/edits/setPartitionScheme';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { DisabledAffordance } from '../common/DisabledAffordance';
import {
  AddPartitionPopover,
  DropPartitionPopover,
  SetPartitionSchemePopover,
} from '../common/PartitionPopovers';
import { SetTableOptionsPopover } from '../common/SetTableOptionsPopover';
import { SetTableTablespacePopover } from '../common/SetTableTablespacePopover';

const NOT_DECLARED = 'Not declared by this statement.';

function tableFromStructure(structure: DdlStructure | null): QualifiedName | null {
  if (!structure) return null;
  if (structure.kind === 'create-table' || structure.kind === 'alter-table') {
    return structure.table;
  }
  return null;
}

/** The engine's own word for a table's storage container. mssql ships
 *  filegroups; pgsql / oracle / mysql ship tablespaces. With no engine bound
 *  (read-only scaffolding) we can't pick, so both terms are shown. */
function storageContainerLabel(engine: SqlEngine | null): string {
  if (engine === null) return 'Tablespace / Filegroup';
  return engine === 'mssql' ? 'Filegroup' : 'Tablespace';
}

export interface StorageTabProps {
  /** Required `data-testid` prefix for unit + E2E specs. */
  readonly testId?: string;
  /** Optional edit context — when supplied, Slice E1 popovers light up. */
  readonly editContext?: VqeDdlEditContext | null;
  /** Parked structure used to resolve the target table for the partition forms. */
  readonly structure?: DdlStructure | null;
  /** Compact density (compact Visual DDL popover): smaller affordance buttons
   *  and tighter section spacing. Structure is unchanged. */
  readonly compact?: boolean;
}

interface StorageRowProps {
  readonly testId: string;
  readonly name: string;
  readonly label: string;
  readonly children: ReactNode;
}

function StorageRow({ testId, name, label, children }: StorageRowProps) {
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid={`${testId}-row-${name}`}
    >
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className="font-mono text-xs text-muted-foreground"
        data-testid={`${testId}-value-${name}`}
        title={NOT_DECLARED}
      >
        —
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-1">{children}</div>
    </div>
  );
}

export function StorageTab({
  testId = 'ddl-storage-tab',
  editContext,
  structure,
  compact = false,
}: StorageTabProps) {
  const targetTable = useMemo(
    () => tableFromStructure(structure ?? null),
    [structure],
  );
  const affordanceClass = compact
    ? 'h-6 gap-1 px-1.5 text-xs text-muted-foreground'
    : undefined;
  const engine = editContext?.service.engineId ?? null;

  // Enabled-first ordering so a permanently-disabled trigger never leads the
  // partitioning row (e.g. on mssql the scheme form is unavailable while
  // add/drop are, and on pgsql it's the reverse).
  const partitionActions = useMemo<readonly ReactNode[]>(() => {
    if (!editContext) return [];
    const eng = editContext.service.engineId;
    const entries = [
      {
        supported: isSetPartitionSchemeSupported(eng),
        node: (
          <SetPartitionSchemePopover
            key="scheme"
            editContext={editContext}
            table={targetTable}
            testId={`${testId}-set-partitioning`}
          />
        ),
      },
      {
        supported: isAddPartitionSupported(eng),
        node: (
          <AddPartitionPopover
            key="add"
            editContext={editContext}
            table={targetTable}
            testId={`${testId}-add-partition`}
          />
        ),
      },
      {
        supported: isDropPartitionSupported(eng),
        node: (
          <DropPartitionPopover
            key="drop"
            editContext={editContext}
            table={targetTable}
            testId={`${testId}-drop-partition`}
          />
        ),
      },
    ];
    return [
      ...entries.filter((e) => e.supported).map((e) => e.node),
      ...entries.filter((e) => !e.supported).map((e) => e.node),
    ];
  }, [editContext, targetTable, testId]);

  return (
    <div className={cn('flex flex-col', compact ? 'gap-2' : 'gap-3')} data-testid={testId}>
      <StorageRow testId={testId} name="partitioning" label="Partitioning">
        {editContext ? (
          partitionActions
        ) : (
          <DisabledAffordance
            testId={`${testId}-set-partitioning`}
            tooltip="Partition editing isn’t available in this read-only view — use “Edit as SQL” to change partitioning."
            variant="ghost"
            ariaLabel="Set partitioning"
            className={affordanceClass}
          >
            <Plus className="h-3.5 w-3.5" />
            Set…
          </DisabledAffordance>
        )}
      </StorageRow>

      <StorageRow
        testId={testId}
        name="tablespace"
        label={storageContainerLabel(engine)}
      >
        {editContext ? (
          <SetTableTablespacePopover
            editContext={editContext}
            testId={`${testId}-set-tablespace`}
          />
        ) : (
          <DisabledAffordance
            testId={`${testId}-set-tablespace`}
            tooltip="Tablespace / filegroup changes aren’t available in this read-only view — use “Edit as SQL” to make them."
            variant="ghost"
            ariaLabel="Set tablespace or filegroup"
            className={affordanceClass}
          >
            <Database className="h-3.5 w-3.5" />
            Set…
          </DisabledAffordance>
        )}
      </StorageRow>

      <StorageRow testId={testId} name="engine-options" label="Engine options">
        {editContext ? (
          <SetTableOptionsPopover
            editContext={editContext}
            table={targetTable}
            testId={`${testId}-set-table-options`}
          />
        ) : (
          <DisabledAffordance
            testId={`${testId}-set-table-options`}
            tooltip="Engine-specific table options aren’t available in this read-only view — use “Edit as SQL” to change them."
            variant="ghost"
            ariaLabel="Edit engine options"
            className={affordanceClass}
          >
            <Settings2 className="h-3.5 w-3.5" />
            Edit…
          </DisabledAffordance>
        )}
      </StorageRow>
    </div>
  );
}
